import { Box, Typography } from '@mui/material'
import { formatDateTime } from 'utils/util'
import { DataTable } from 'components/Shared/DataTable'
import { useNavigate } from 'react-router-dom'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { type SweepstakeWinner } from 'types/api'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useState } from 'react'
import { ErrorIndicator } from 'components/ErrorIndicator/ErrorIndicator'
import { useGetAllLocationSweepstakeWinners } from 'hooks/api/Sweepstake/useGetAllLocationSweepstakeWinners'
import { groupPermissionStore } from 'stores/groupPermissionStore'

const columns: Array<GridColDef<SweepstakeWinner>> = [
  {
    field: 'endDate',
    headerName: 'Sweepstake End Date/Time',
    minWidth: 150,
    flex: 1,
    type: 'dateTime',
    sortable: true,
    sortingOrder: ['desc', 'asc'],
    valueGetter: (params) =>
      params.row.sweepstakeDrawing?.contest?.endDate
        ? new Date(params.row.sweepstakeDrawing.contest.endDate)
        : null,
    renderCell: (params) => <Box>{formatDateTime(params.value, 'P p')}</Box>,
  },
  {
    field: 'name',
    headerName: 'Sweepstake Name',
    minWidth: 230,
    flex: 1,
    sortingOrder: ['desc', 'asc'],
    valueGetter: (params) => params.row.sweepstakeDrawing?.contest?.name ?? '',
    sortComparator: (v1, v2) => v1.localeCompare(v2),
    sortable: true,
  },
  {
    field: 'fullName',
    headerName: 'Winner Name',
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => params.row.patron?.firstName ?? '',
    renderCell: (params) => (
      <Box>
        {params.row.patron?.firstName} {params.row.patron?.lastName}
      </Box>
    ),
  },
  {
    field: 'dateDrawn',
    headerName: 'Date Drawn',
    minWidth: 150,
    flex: 1,
    sortable: true,
    sortingOrder: ['desc', 'asc'],
    renderCell: (params) => (
      <Box>
        {params.row.dateDrawn
          ? `${String(formatDateTime(params.row.dateDrawn, 'P p'))}`
          : null}
      </Box>
    ),
  },
]

export const DashboardLocationSweepValidationGrid = () => {
  const navigate = useNavigate()
  const query = useGetAllLocationSweepstakeWinners()
  const [filteredData, setFilteredData] = useState<SweepstakeWinner[]>()
  const permissions = groupPermissionStore.getState()

  let locationSweepstakeWinners = query.data ?? []

  locationSweepstakeWinners = locationSweepstakeWinners.map((winner) => ({
    ...winner,
    fullName:
      `${winner.patron?.firstName ?? ''} ${winner.patron?.lastName ?? ''}`.trim(),
  }))

  const handleFilteredData = (filteredData: SweepstakeWinner[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    const patronId = params.row.patron?.id as number
    const contestId = params.row.contestEntry?.contest?.id as number

    if (
      permissions.patronAccountsPermission &&
      params.field === 'fullName' &&
      patronId !== null
    ) {
      navigate(`/PatronAccounts/${patronId}/Overview`, {
        state: { sweepstake: params.row },
      })
    }
    if (
      permissions.contests_SweepstakesPermission &&
      params.field === 'name' &&
      params.row.contestEntry.contest.id !== null
    ) {
      navigate(`/Contests/LocationSweepstakes/${contestId}/Overview`, {
        state: { sweepstake: params.row },
      })
    }
  }

  return (
    <>
      <Typography mb={2} variant="h2">
        {'Location Sweepstakes / Location Validation'}
      </Typography>

      {(query.isPending || query.isFetching) && <ActivityIndicator />}

      {query.isError && <ErrorIndicator />}

      {(!query.isPending || !query.isFetching) && locationSweepstakeWinners && (
        <>
          <GridSearchField<SweepstakeWinner>
            placeholder="Search Location Sweepstakes"
            data={locationSweepstakeWinners}
            handleFilteredData={handleFilteredData}
            fullWidth
          />

          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                maxHeight: 350,
                minHeight: 200,
              }}
            >
              <DataTable
                autoHeight={false}
                hideFooter={false}
                rowHeight={50}
                sortFieldName="endDate"
                sort="desc"
                columns={columns}
                rows={filteredData ?? locationSweepstakeWinners}
                onCellClick={selectCell}
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}
